import {
  type Scene,
  type Object3D,
  Box3,
  Vector3,
  AnimationMixer,
  type AnimationAction,
  type AnimationClip,
  MathUtils,
  Mesh,
  PlaneGeometry,
  MeshStandardMaterial,
  DoubleSide,
  type Texture,
} from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { clone as skeletonClone } from 'three/examples/jsm/utils/SkeletonUtils.js';

type Kind = 'sky' | 'bird' | 'dino' | 'cover';
interface Def {
  name: string;
  url: string;
  kind: Kind;
  size: number; // целевой габарит (нормировка)
  speed: [number, number]; // u/s диапазон
  yOff: [number, number]; // высота относительно centerY
}

// CC0/CC-BY low-poly модели в /models. Птицы и динозавр анимированы.
const DEFS: Def[] = [
  { name: 'Ufo', url: '/models/Ufo.glb', kind: 'sky', size: 12, speed: [6, 9], yOff: [4, 26] },
  { name: 'Blimp', url: '/models/Blimp.glb', kind: 'sky', size: 22, speed: [3, 5], yOff: [8, 30] },
  { name: 'Plane', url: '/models/Plane.glb', kind: 'sky', size: 11, speed: [10, 15], yOff: [0, 24] },
  { name: 'Ship', url: '/models/Ship.glb', kind: 'sky', size: 15, speed: [4, 7], yOff: [2, 26] },
  { name: 'Flamingo', url: '/models/Flamingo.glb', kind: 'bird', size: 6, speed: [12, 18], yOff: [-6, 18] },
  { name: 'Parrot', url: '/models/Parrot.glb', kind: 'bird', size: 5.5, speed: [12, 18], yOff: [-6, 18] },
  { name: 'Stork', url: '/models/Stork.glb', kind: 'bird', size: 7, speed: [11, 16], yOff: [-4, 20] },
  { name: 'Dino', url: '/models/Dino.glb', kind: 'dino', size: 10, speed: [9, 14], yOff: [-12, 2] },
];

type Phase = 'enter' | 'linger' | 'exit';
interface Flyer {
  obj: Object3D;
  mixer: AnimationMixer | null;
  runAct: AnimationAction | null; // для динозавра — бег
  idleAct: AnimationAction | null; // стоит
  kind: Kind;
  dir: number;
  cruise: number;
  stageX: number;
  phase: Phase;
  lingerDur: number; // ms
  phaseEnd: number;
  bob: number;
  circleC: Vector3 | null;
  circleR: number;
  circleA: number;
}
interface Template {
  def: Def;
  obj: Object3D;
  clips: AnimationClip[];
  scale: number;
}

/**
 * Сказочные пролёты, ЭПИЗОДАМИ с паузами (не толпой): одно событие за раз, потом
 * пустое небо 6–16с. Динозавр забегает → стоит → убегает; птицы прилетают →
 * кружат → улетают; НЛО зависает → улетает; дирижабль/самолёт/корабль медленно
 * проплывают. glTF (CC0), грузятся лениво.
 */
export class FlyBys {
  private templates: Template[] = [];
  private active: Flyer[] = [];
  private phase: 'pause' | 'event' = 'pause';
  private pauseUntil = 0;
  private readonly span = 78;
  private readonly flock: number;
  private coversEnabled = true; // гигантские обложки-пролёты (дев-панель)
  private freqMult = 1; // частота пролётов (дев-панель): >1 — чаще, <1 — реже

  constructor(
    private scene: Scene,
    private centerY: number,
    mobile: boolean,
    private coverTex: (() => Texture | null) | null = null,
  ) {
    this.flock = mobile ? 2 : 4;
    const loader = new GLTFLoader();
    for (const def of DEFS) {
      loader.load(
        def.url,
        (gltf) => {
          const obj = gltf.scene;
          const box = new Box3().setFromObject(obj);
          const s = new Vector3();
          box.getSize(s);
          const maxDim = Math.max(s.x, s.y, s.z) || 1;
          obj.traverse((o) => {
            o.castShadow = false;
            o.frustumCulled = false;
          });
          this.templates.push({ def, obj, clips: gltf.animations || [], scale: def.size / maxDim });
        },
        undefined,
        () => {
          /* модель не загрузилась — пропускаем */
        },
      );
    }
  }

  private makeFlyer(t: Template, flockOffset = 0): Flyer {
    const obj = skeletonClone(t.obj);
    obj.scale.setScalar(t.scale);
    const dir = Math.random() < 0.5 ? 1 : -1;
    const y = this.centerY + MathUtils.randFloat(t.def.yOff[0], t.def.yOff[1]);
    const z = MathUtils.randFloat(-22, 22) + flockOffset;
    obj.position.set(-dir * this.span, y, z);
    obj.rotation.y = dir > 0 ? Math.PI / 2 : -Math.PI / 2;

    let mixer: AnimationMixer | null = null;
    let runAct: AnimationAction | null = null;
    let idleAct: AnimationAction | null = null;
    if (t.clips.length) {
      mixer = new AnimationMixer(obj);
      if (t.def.kind === 'dino') {
        const run = t.clips.find((c) => /run/i.test(c.name)) || t.clips.find((c) => /walk/i.test(c.name)) || t.clips[0];
        const idle = t.clips.find((c) => /idle/i.test(c.name)) || run;
        runAct = mixer.clipAction(run);
        idleAct = idle !== run ? mixer.clipAction(idle) : null;
        runAct.play();
      } else {
        const a = mixer.clipAction(t.clips[0]);
        a.timeScale = MathUtils.randFloat(0.85, 1.25);
        a.play();
      }
    }

    // длительность «зависания» по типу
    let lingerDur = 0;
    if (t.def.kind === 'bird') lingerDur = MathUtils.randFloat(4200, 6800);
    else if (t.def.kind === 'dino') lingerDur = MathUtils.randFloat(2800, 4600);
    else if (t.def.name === 'Ufo') lingerDur = MathUtils.randFloat(2600, 4200);

    return {
      obj,
      mixer,
      runAct,
      idleAct,
      kind: t.def.kind,
      dir,
      cruise: MathUtils.randFloat(t.def.speed[0], t.def.speed[1]),
      stageX: MathUtils.randFloat(-9, 9) + flockOffset * 0.5,
      phase: 'enter',
      lingerDur,
      phaseEnd: 0,
      bob: Math.random() * Math.PI * 2,
      circleC: null,
      circleR: MathUtils.randFloat(6, 12),
      circleA: Math.random() * Math.PI * 2,
    };
  }

  /** Дев-панель: вкл/выкл гигантские обложки-пролёты. */
  setCoversEnabled(on: boolean): void {
    this.coversEnabled = on;
  }
  /** Дев-панель: частота пролётов (0.2…3), множит «обратную паузу». */
  setFrequency(mult: number): void {
    this.freqMult = MathUtils.clamp(mult, 0.2, 3);
  }

  private makeCoverFlyer(tex: Texture): Flyer {
    const size = MathUtils.randFloat(9, 15);
    const mat = new MeshStandardMaterial({
      map: tex,
      color: 0xffffff,
      roughness: 0.5,
      metalness: 0.0,
      side: DoubleSide,
    });
    const obj = new Mesh(new PlaneGeometry(size, size), mat);
    const dir = Math.random() < 0.5 ? 1 : -1;
    const y = this.centerY + MathUtils.randFloat(2, 24);
    const z = MathUtils.randFloat(-18, 12);
    obj.position.set(-dir * this.span, y, z);
    obj.rotation.y = MathUtils.randFloat(-0.3, 0.3);
    return {
      obj,
      mixer: null,
      runAct: null,
      idleAct: null,
      kind: 'cover',
      dir,
      cruise: MathUtils.randFloat(3.5, 6),
      stageX: MathUtils.randFloat(-8, 8),
      phase: 'enter',
      lingerDur: MathUtils.randFloat(3800, 6200),
      phaseEnd: 0,
      bob: Math.random() * Math.PI * 2,
      circleC: null,
      circleR: 0,
      circleA: 0,
    };
  }

  private startEvent(): void {
    // иногда — гигантская обложка текущей фонотеки (если включено и есть текстура)
    const tex = this.coverTex?.() ?? null;
    if (this.coversEnabled && tex && Math.random() < 0.34) {
      const f = this.makeCoverFlyer(tex);
      this.scene.add(f.obj);
      this.active.push(f);
      return;
    }
    const t = this.templates[Math.floor(Math.random() * this.templates.length)];
    const n = t.def.kind === 'bird' ? this.flock : 1;
    for (let i = 0; i < n; i++) {
      const f = this.makeFlyer(t, n > 1 ? (i - (n - 1) / 2) * 6 : 0);
      this.scene.add(f.obj);
      this.active.push(f);
    }
  }

  update(dt: number, now: number): void {
    if (this.phase === 'event' && this.active.length === 0) {
      this.phase = 'pause';
      this.pauseUntil = now + MathUtils.randFloat(6000, 16000) / this.freqMult; // пустое небо
    }
    if (this.phase === 'pause' && now >= this.pauseUntil && this.templates.length) {
      this.startEvent();
      this.phase = 'event';
    }
    for (let i = this.active.length - 1; i >= 0; i--) {
      const fl = this.active[i];
      if (this.step(fl, dt, now)) {
        this.scene.remove(fl.obj);
        fl.mixer?.stopAllAction();
        if (fl.kind === 'cover') {
          const m = fl.obj as Mesh;
          m.geometry.dispose();
          (m.material as MeshStandardMaterial).dispose(); // текстуру (общую) не трогаем
        }
        this.active.splice(i, 1);
      }
    }
  }

  /** Шаг одного пролёта; true → улетел, удалить. */
  private step(f: Flyer, dt: number, now: number): boolean {
    const p = f.obj.position;
    f.mixer?.update(dt);
    if (f.phase === 'enter') {
      p.x += f.cruise * f.dir * dt;
      p.y += Math.sin(now * 0.001 + f.bob) * dt * 0.5;
      if (f.kind === 'cover') f.obj.rotation.y += dt * 0.5; // обложка крутится
      const reached = f.dir > 0 ? p.x >= f.stageX : p.x <= f.stageX;
      if (reached) {
        if (f.lingerDur > 0) {
          f.phase = 'linger';
          f.phaseEnd = now + f.lingerDur;
          f.circleC = new Vector3(p.x, p.y, p.z);
          if (f.kind === 'dino' && f.idleAct && f.runAct) {
            f.idleAct.reset().fadeIn(0.3).play();
            f.runAct.fadeOut(0.3);
          }
        } else {
          f.phase = 'exit';
        }
      }
    } else if (f.phase === 'linger') {
      if (f.kind === 'bird' && f.circleC) {
        f.circleA += dt * 1.1; // кружит
        p.x = f.circleC.x + Math.cos(f.circleA) * f.circleR;
        p.z = f.circleC.z + Math.sin(f.circleA) * f.circleR;
        p.y = f.circleC.y + Math.sin(now * 0.0018 + f.bob) * 1.2;
        f.obj.rotation.y = -f.circleA; // по касательной
      } else if (f.kind === 'dino') {
        // стоит
      } else {
        // НЛО — зависает и покачивается
        p.y += Math.sin(now * 0.0016 + f.bob) * dt * 1.2;
        f.obj.rotation.y += dt * 0.4;
      }
      if (now >= f.phaseEnd) {
        f.phase = 'exit';
        if (f.kind === 'dino' && f.idleAct && f.runAct) {
          f.runAct.reset().fadeIn(0.3).play();
          f.idleAct.fadeOut(0.3);
        }
      }
    } else {
      // exit
      p.x += f.cruise * f.dir * dt;
      p.y += Math.sin(now * 0.001 + f.bob) * dt * 0.5;
      if (Math.abs(p.x) > this.span + 6) return true;
    }
    return false;
  }
}
